import React from "react";
import { Building2 } from "lucide-react";
import { apiFetch } from "../lib/apiFetch";

interface CompanyIconProps {
  ticker: string;
  domain?: string;
  size?: number;
  className?: string;
}

// In-memory cache for verified, working logo URLs during runtime session
const logoMemoryCache: Record<string, string> = {};

function extractCleanDomain(domainStr?: string): string | null {
  if (!domainStr || typeof domainStr !== "string") return null;
  try {
    let clean = domainStr.trim().toLowerCase();
    clean = clean.replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
    return clean.length > 2 ? clean : null;
  } catch {
    return null;
  }
}

function extractRootDomain(cleanDomain?: string | null): string | null {
  if (!cleanDomain) return null;
  const noWww = cleanDomain.replace(/^www\./i, "");
  // Remove known non-apex prefixes like ir., investor., investors., corp., corporate., news., global., m., en., about., intl., invest.
  const stripped = noWww.replace(/^(ir|investor|investors|corp|corporate|news|global|m|en|about|intl|invest)\./i, "");
  return stripped.length > 2 ? stripped : noWww;
}

function getGoogleFaviconUrl(domainStr: string): string {
  const clean = domainStr.trim().toLowerCase().replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(clean)}&sz=128`;
}

function getInitialCandidates(cleanTicker: string, domainStr?: string): string[] {
  if (!cleanTicker || cleanTicker.startsWith("^") || cleanTicker.includes("%5E")) return [];
  
  const clean = extractCleanDomain(domainStr);
  const root = extractRootDomain(clean);
  const isUS = !cleanTicker.includes(".");
  const candidates: string[] = [];
  const added = new Set<string>();

  const add = (url: string) => {
    if (url && !added.has(url)) {
      added.add(url);
      candidates.push(url);
    }
  };

  if (clean) {
    add(getGoogleFaviconUrl(clean));
  }
  if (root) {
    add(getGoogleFaviconUrl(root));
  }
  if (isUS) {
    add(`https://financialmodelingprep.com/image-stock/${encodeURIComponent(cleanTicker)}.png`);
  }
  add(`https://assets.parqet.com/logos/symbol/${encodeURIComponent(cleanTicker)}`);
  if (root && !root.startsWith("www.")) {
    add(getGoogleFaviconUrl(`www.${root}`));
  }
  
  return candidates;
}

export const CompanyIcon: React.FC<CompanyIconProps> = ({
  ticker,
  domain,
  size = 28,
  className = "",
}) => {
  const cleanTicker = (ticker || "").trim().toUpperCase();
  const isIndex = cleanTicker.startsWith("^") || cleanTicker.includes("%5E");
  const cacheKey = `logo_url_${cleanTicker}`;

  // Check synchronous caches on initial render for instant zero-latency loading
  const getCachedUrl = React.useCallback((): string | null => {
    if (!cleanTicker || isIndex) return null;
    if (logoMemoryCache[cleanTicker]) {
      return logoMemoryCache[cleanTicker];
    }
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        logoMemoryCache[cleanTicker] = stored;
        return stored;
      }
    } catch {}
    return null;
  }, [cleanTicker, isIndex, cacheKey]);

  const [candidates, setCandidates] = React.useState<string[]>(() => {
    const cached = getCachedUrl();
    if (cached) return [cached];
    return getInitialCandidates(cleanTicker, domain);
  });

  const [candidateIndex, setCandidateIndex] = React.useState<number>(0);
  const [hasFailed, setHasFailed] = React.useState<boolean>(false);

  // Synchronize when ticker or domain changes
  React.useEffect(() => {
    if (isIndex || !cleanTicker) {
      setHasFailed(true);
      return;
    }

    const cached = getCachedUrl();
    if (cached) {
      setCandidates([cached]);
      setCandidateIndex(0);
      setHasFailed(false);
      return;
    }

    const initial = getInitialCandidates(cleanTicker, domain);
    if (initial.length > 0) {
      setCandidates(initial);
      setCandidateIndex(0);
      setHasFailed(false);
    } else {
      // If domain is not provided yet, fetch candidate URLs from /api/logo
      let isCancelled = false;
      apiFetch(`/api/logo?ticker=${encodeURIComponent(cleanTicker)}`)
        .then((res) => res.json())
        .then((data) => {
          if (isCancelled) return;
          if (data?.logoUrls && Array.isArray(data.logoUrls) && data.logoUrls.length > 0) {
            setCandidates(data.logoUrls);
            setCandidateIndex(0);
            setHasFailed(false);
          } else if (data?.logoUrl) {
            setCandidates([data.logoUrl, `https://assets.parqet.com/logos/symbol/${encodeURIComponent(cleanTicker)}`]);
            setCandidateIndex(0);
            setHasFailed(false);
          } else {
            setCandidates([`https://assets.parqet.com/logos/symbol/${encodeURIComponent(cleanTicker)}`]);
            setCandidateIndex(0);
            setHasFailed(false);
          }
        })
        .catch(() => {
          if (isCancelled) return;
          setCandidates([`https://assets.parqet.com/logos/symbol/${encodeURIComponent(cleanTicker)}`]);
          setCandidateIndex(0);
          setHasFailed(false);
        });

      return () => {
        isCancelled = true;
      };
    }
  }, [cleanTicker, domain, isIndex, getCachedUrl]);

  const activeSrc = candidates[candidateIndex] || null;

  const handleError = () => {
    // Purge bad URL from cache if it failed
    delete logoMemoryCache[cleanTicker];
    try {
      localStorage.removeItem(cacheKey);
    } catch {}

    if (candidateIndex + 1 < candidates.length) {
      setCandidateIndex((prev) => prev + 1);
    } else {
      // If we were on a stale single cached URL that failed, fetch fresh candidate ladder
      if (candidates.length <= 1) {
        apiFetch(`/api/logo?ticker=${encodeURIComponent(cleanTicker)}&refresh=true`)
          .then((res) => res.json())
          .then((data) => {
            if (data?.logoUrls && Array.isArray(data.logoUrls) && data.logoUrls.length > 0) {
              setCandidates(data.logoUrls);
              setCandidateIndex(0);
            } else {
              setHasFailed(true);
            }
          })
          .catch(() => {
            setHasFailed(true);
          });
      } else {
        setHasFailed(true);
      }
    }
  };

  const handleLoad = () => {
    if (activeSrc && cleanTicker) {
      logoMemoryCache[cleanTicker] = activeSrc;
      try {
        localStorage.setItem(cacheKey, activeSrc);
      } catch {}
    }
  };

  if (hasFailed || isIndex || !activeSrc) {
    return (
      <div
        className={`bg-zinc-800/50 rounded-sm text-zinc-500 flex items-center justify-center shrink-0 select-none ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
        title={cleanTicker}
        aria-label={`${cleanTicker} default logo`}
      >
        <Building2 size={Math.max(12, Math.floor(size * 0.5))} className="opacity-50" />
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-center justify-center shrink-0 rounded-sm overflow-hidden bg-transparent ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <div className="absolute inset-0 flex items-center justify-center bg-zinc-800/50 text-zinc-500 -z-10">
        <Building2 size={Math.max(12, Math.floor(size * 0.5))} className="opacity-50" />
      </div>
      <img
        key={activeSrc}
        src={activeSrc}
        alt={`${cleanTicker} logo`}
        width={size}
        height={size}
        className="w-full h-full object-contain p-0 relative z-10"
        style={{ color: 'transparent' }}
        onError={handleError}
        onLoad={handleLoad}
        loading="lazy"
      />
    </div>
  );
};
